import { UiState } from "@/components/enums/UiState";
import { ifError } from "assert";
import axios from "axios";
import { inject, observer } from "mobx-react";
import { createContext, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Store } from "stores/store";

interface ImageProcessingContextValues {
    requestToken: () => Promise<void>;
    submitImage: (blob: Blob) => Promise<void>;
    cancelProcessing: () => void;
    processedImageURL: string | null
}

const ImageProcessingContext = createContext<ImageProcessingContextValues>({
    requestToken: async () => { },
    submitImage: async (blob) => { },
    cancelProcessing: () => { },
    processedImageURL: null
})

type ImageProcessingProps = {
    children: ReactNode;
    store?: Store;
}

type ApiTokenResponse = {
    token: string;
    timestamp: string;
}

type ApiStatusResponse = {
    position: number;
}

const ImageProcessing = inject("store")(observer(
    ({ children, store }: ImageProcessingProps): JSX.Element => {

        if (!store) {
            throw Error("Component was not setup correctly");
        }

        const {
            updateImageAgedUrl,
            updateImagePresentUrl,
            updateCaptureFace,
            updateRenderContent,
            updateImageProcessingState,
        } = store;

        const timeOfLastRequest = useRef<Date | null>(null)
        let timeoutOfNextScheduledCall: NodeJS.Timeout | null = null;
        function noStressFetch<T>(cooldownMilliseconds: number, url: string, options: any) {
            return new Promise<T>(async (resolve, reject) => {
                try {
                    const now = new Date()
                    if (timeOfLastRequest.current == null || now.getTime() - timeOfLastRequest.current.getTime() > cooldownMilliseconds) {
                        console.debug('ImageProcessing: fetch now')
                        // either no request has been sent or the last request is at least 5sek in the past
                        timeOfLastRequest.current = now
                        const res = await fetch(url, options)
                        if (!res.ok) throw new Error(await res.text() || res.statusText)
                        const json = await res.json()
                        resolve(json)
                        /*} else if (now.getTime() - timeOfLastRequest.current.getTime() < 0) {
                            console.debug('ImageProcessing: next fetch is already scheduled')
                            // time of last request is in the future, i.e., it is scheduled for submission
                            reject(new Error('next fetch is already scheduled'))
                        */
                    } else {
                        // we are in the waiting zone of 0sek < x < 5sek
                        if (timeoutOfNextScheduledCall) clearTimeout(timeoutOfNextScheduledCall)
                        const delta = now.getTime() - timeOfLastRequest.current.getTime()
                        const relaxForMilliseconds = cooldownMilliseconds - delta
                        console.log(cooldownMilliseconds, delta, cooldownMilliseconds - delta)
                        console.debug('ImageProcessing: scheduling next fetch to start in ms:', relaxForMilliseconds)
                        timeoutOfNextScheduledCall = setTimeout(async () => {
                            try {
                                if (token.current === '') {
                                    reject()
                                    return
                                }
                                console.debug('ImageProcessing: scheduled fetch is invoked now')
                                const res = await fetch(url, options)
                                if (!res.ok) throw new Error(await res.text() || res.statusText)
                                const json = await res.json()
                                resolve(json)
                            } catch (e) {
                                //console.error(e)
                                reject(e)
                            }
                        }, relaxForMilliseconds)
                        timeOfLastRequest.current = new Date(now.getTime() + relaxForMilliseconds)
                    }
                } catch (e) {
                    //console.error(e)
                    reject(e)
                }
            })
        }

        function reset() {
            token.current = ''

            timeOfLastRequest.current = null

            if (timeoutOfNextScheduledCall) clearTimeout(timeoutOfNextScheduledCall)

            updateImageAgedUrl(undefined)
            updateImagePresentUrl(undefined)

            updateImageProcessingState({
                positionInQueue: undefined,
                tokenAcquired: false,
                noConnection: false,
                noFaceFound: false,
                waitingToSubmit: false,
                waitingForResult: false,
                processingAborted: false,
                processingComplete: false,
            })
        }

        const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";
        const token = useRef<string>('')
        const tokenRequest = useRef<Promise<void> | null>(null);
        const [processedImageURL, setProcessedImageURL] = useState<string | null>(null);

        // const QUEUE_BACKEND = process.env.NEXT_PUBLIC_QUEUE_BACKEND_URL ?? 'https://nl-queue.curious.zone'
        const QUEUE_BACKEND = process.env.NEXT_PUBLIC_QUEUE_BACKEND_URL ?? 'https://nl-backend.site'
        // const IMAGE_BUCKET = process.env.NEXT_PUBLIC_IMAGE_BUCKET_URL ?? 'https://nl-minio.curious.zone/testbucket/processed%2F'
        const IMAGE_BUCKET = process.env.NEXT_PUBLIC_IMAGE_BUCKET_URL ?? 'https://minio.nl-backend.site/testbucket/processed%2F'

        /* 
            1 Request ('api/token') - Beim Foto aufnehmen starten wenn der Countdown los geht
            2 Request ('api/status') - Wenn das Foto wurde aufgenommen, und wurde korrekt bearbeitet, wird wiederholt bis queue position bei 20 oder drunter ist
            3 Request('api/image') - Wenn Queue position rihctig ist, einmal. Bild muss format 512x512px und ein png file sein.
            4 Request('api/status') - So lange bis du keine Response mehr bekommst, bzw. einen 404 fehler weil der token nicht mehr gefunden wurde
            5 Image url aus token und .png auf s3 Bucket zusammen sezten als string.
        */

        const requestToken = useCallback(async () => {
            console.debug('ImageProcessing: request token')

            if (!store) {
                throw Error("Component was not setup correctly");
            }

            reset()

            tokenRequest.current = noStressFetch<ApiTokenResponse>(5000, QUEUE_BACKEND + '/api/token', {
                method: 'GET',
                headers: { "Content-Type": "application/json", }
            }).then(json => {
                console.log(json.token)
                token.current = json.token
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    tokenAcquired: true,
                })
            }).catch(e => {
                console.error('ImageProcessing: token request failed', e)
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    noConnection: true,
                })
            }).finally(() => {
                tokenRequest.current = null;
            })

            await tokenRequest.current;
        }, []);

        function waitInQueueForProcessing() {
            console.debug('ImageProcessing: waitInQueueForProcessing')

            if (!store) {
                throw Error("Component was not setup correctly");
            }

            updateImageProcessingState({
                ...store.imageProcessingState,
                waitingToSubmit: true,
            })

            return new Promise(async (resolve, reject) => {
                try {
                    let json: ApiStatusResponse | null = null;
                    do {
                        if (token.current === '') {
                            reject()
                            return
                        }

                        json = await noStressFetch<ApiStatusResponse>(5000, QUEUE_BACKEND + '/api/status', {
                            method: 'POST',
                            body: JSON.stringify({ 'token': token.current }),
                            headers: { "Content-Type": "application/json", }
                        })
                        console.log(json)
                        updateImageProcessingState({
                            ...store.imageProcessingState,
                            positionInQueue: json.position
                        })
                    } while (json.position > 20)
                    updateImageProcessingState({
                        ...store.imageProcessingState,
                        positionInQueue: undefined,
                        waitingToSubmit: false,
                    })
                    resolve(true)
                } catch (e) {
                    //console.error(e)
                    reject(e)
                }
            })
        }

        function waitForImageToBeReady() {
            console.debug('ImageProcessing: waitForImageToBeReady')

            if (!store) {
                throw Error("Component was not setup correctly");
            }

            updateImageProcessingState({
                ...store.imageProcessingState,
                waitingForResult: true,
            })

            return new Promise(async (resolve, reject) => {
                try {
                    let json: ApiStatusResponse | null = null;
                    do {
                        if (token.current === '') {
                            reject()
                            return
                        }

                        json = await noStressFetch<ApiStatusResponse>(5000, QUEUE_BACKEND + '/api/status', {
                            method: 'POST',
                            body: JSON.stringify({ 'token': token.current }),
                            headers: { "Content-Type": "application/json", }
                        })
                        console.log(json)

                    } while (json.position)
                    updateImageProcessingState({
                        ...store.imageProcessingState,
                        waitingForResult: false,
                    })
                    resolve(true)
                } catch (e) {
                    console.debug(e)
                    updateImageProcessingState({
                        ...store.imageProcessingState,
                        waitingForResult: false,
                    })
                    if (String(e).includes("Could not find face")) {
                        reject(e)
                        return
                    }
                    resolve(true)
                }
            })
        }

        const submitImage = useCallback(async (blob: Blob) => {
            console.log("TOKEN AT SUBMIT TIME:", token.current);

            console.debug('ImageProcessing: submit image', blob)

            if (!store) {
                throw Error("Component was not setup correctly");
            }

            if (token.current === '') {
                if (tokenRequest.current !== null) {
                    await tokenRequest.current;
                } else {
                    await requestToken();
                }
            }

            if (token.current === '') {
                console.error('ImageProcessing: image submission aborted because no token is available')
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    noConnection: true,
                })
                return;
            }

            try {
                await waitInQueueForProcessing();
                console.debug('ImageProcessing: sending image, as position in queue is now')

                const activeToken = token.current;
                const formData = new FormData()
                formData.append('image', blob)
                formData.append('token', activeToken)

                try {
                    const json = await noStressFetch(5000, QUEUE_BACKEND + '/api/image', {
                        method: 'POST',
                        body: formData,
                    })
                    console.log(json)

                    const url = IMAGE_BUCKET + activeToken + '.png'
                    const urlCutout = IMAGE_BUCKET + activeToken + '_cutout.png'

                    console.debug('ImageProcessing: the image should be coming up here:', url)
                    await waitForImageToBeReady();
                    console.debug('ImageProcessing: the image is processed and ready here:', url)

                    if (token.current === '') return;

                    updateImageProcessingState({
                        ...store.imageProcessingState,
                        processingComplete: true,
                        positionInQueue: undefined,
                        noConnection: false,
                        noFaceFound: false,
                        processingAborted: false,
                        waitingForResult: false,
                        waitingToSubmit: false,
                    })

                    setProcessedImageURL(url);
                    updateImageAgedUrl(url);
                    updateImagePresentUrl(urlCutout);
                    updateCaptureFace(false);

                    console.debug('ImageProcessing: final status:', store.imageProcessingState)
                } catch (e) {
                    console.error("ImageProcessing: image submission failed", e);

                    if (String(e).includes("Could not find face")) {
                        updateImageProcessingState({
                            ...store.imageProcessingState,
                            noFaceFound: true,
                        });
                    } else {
                        updateImageProcessingState({
                            ...store.imageProcessingState,
                            noConnection: true,
                        });
                    }
                }
            } catch (e) {
                console.error('ImageProcessing: waitInQueueForProcessing failed', e)
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    noConnection: true,
                })
            }

        }, [requestToken]);

        const cancelProcessing = useCallback(() => {
            reset()
        }, []);

        useEffect(() => {
            console.debug('ImageProcessing: mounted')
            return () => {
                console.debug('ImageProcessing: unmounted')
            }
        }, [])

        return <ImageProcessingContext.Provider value={{
            requestToken,
            submitImage,
            cancelProcessing,
            processedImageURL
        }}>
            {children}
        </ImageProcessingContext.Provider>
    }))

export { ImageProcessing, ImageProcessingContext };
